import { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { useToast } from '../hooks/use-toast';
import { BookOpen, ChevronRight, ChevronLeft, CheckCircle2, Calculator, CalendarCheck, Search, Loader2 } from 'lucide-react';
import { apiUrl } from '../config/api';
import { calculateEnrollmentPrice } from '@readwell/pricing';

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

const ORG_TYPES = [
  { id: 'district', label: 'District' },
  { id: 'school', label: 'School' },
  { id: 'partner', label: 'Partner' },
];

const STEPS = {
  district: ['orgInfo', 'primaryContact', 'additionalContacts', 'review'],
  school:   ['orgInfo', 'primaryContact', 'additionalContacts', 'review'],
  partner:  ['partnerForm', 'review'],
};

const STEP_TITLES = {
  orgInfo: 'Organization Info',
  primaryContact: 'Primary Contact',
  additionalContacts: 'Additional Contacts',
  partnerForm: 'Partner Sign Up',
  review: 'Review & Submit',
};

export default function DistrictSignup() {
  const { toast } = useToast();
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pricingRevealed, setPricingRevealed] = useState(false);

  const [lookupResults, setLookupResults] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [enrollmentSource, setEnrollmentSource] = useState('');
  const lookupTimer = useRef(null);

  const [formData, setFormData] = useState({
    orgType: 'district',
    districtName: '',
    state: '',
    programType: '',
    totalEnrollment: '',
    primaryContactName: '', primaryContactTitle: '', primaryContactEmail: '', primaryContactPhone: '',
    curriculumDirectorName: '', curriculumDirectorEmail: '', curriculumDirectorPhone: '',
    techDirectorName: '', techDirectorEmail: '', techDirectorPhone: '',
    partnerAttestation: false,
    partnerUseCase: '',
    partnerAccountCount: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  const orgType = formData.orgType;
  const steps = STEPS[orgType];
  const currentStepKey = steps[stepIndex];
  const totalSteps = steps.length;

  const enrollmentPricing = calculateEnrollmentPrice(formData.totalEnrollment);
  const partnerPrice = formData.partnerAttestation
    ? { price: 0, program: 'Reading Disability Program (Free)' }
    : { price: parseInt(formData.partnerAccountCount) || 0, program: '$1/account per year' };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const setOrgType = (type) => {
    setFormData(prev => ({ ...prev, orgType: type }));
    setStepIndex(0);
    setPricingRevealed(false);
    setErrors({});
  };

  const validateEmail = (email) => EMAIL_RE.test(email);

  // NCES district lookup — debounced search
  const handleDistrictSearch = useCallback((name) => {
    updateField('districtName', name);
    setEnrollmentSource('');
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    if (!name || name.length < 3 || !formData.state) {
      setLookupResults([]);
      return;
    }
    lookupTimer.current = setTimeout(async () => {
      setLookupLoading(true);
      try {
        const resp = await fetch(apiUrl(`/api/districts/lookup?q=${encodeURIComponent(name)}&state=${formData.state}`));
        if (resp.ok) {
          const data = await resp.json();
          setLookupResults(data);
        }
      } catch { /* graceful — form works without lookup */ }
      finally { setLookupLoading(false); }
    }, 300);
  }, [formData.state]);

  // When user selects a district from the lookup dropdown
  const selectDistrict = async (district) => {
    setFormData(prev => ({ ...prev, districtName: district.name }));
    setLookupResults([]);
    // Fetch grades 4-12 enrollment
    try {
      const resp = await fetch(apiUrl(`/api/districts/enrollment/${district.leaid}`));
      if (resp.ok) {
        const data = await resp.json();
        if (data.total > 0) {
          setFormData(prev => ({ ...prev, totalEnrollment: String(data.total) }));
          setEnrollmentSource(`NCES ${district.name} — grades 4-12`);
          setPricingRevealed(false);
        }
      }
    } catch { /* enrollment fetch failed — user enters manually */ }
  };

  const validateCurrentStep = () => {
    const e = {};
    const key = currentStepKey;

    if (key === 'orgInfo') {
      const label = orgType === 'school' ? 'School name' : 'District name';
      if (!formData.districtName.trim()) e.districtName = `${label} is required`;
      if (!formData.state) e.state = 'State is required';
      if (orgType === 'district' && !formData.programType) e.programType = 'Please select a program type';
      if (!formData.totalEnrollment || parseInt(formData.totalEnrollment) <= 0) {
        e.totalEnrollment = 'Grades 4–12 enrollment is required';
      }
    }

    if (key === 'primaryContact') {
      if (!formData.primaryContactName.trim()) e.primaryContactName = 'Name is required';
      if (!formData.primaryContactEmail.trim()) {
        e.primaryContactEmail = 'Email is required';
      } else if (!validateEmail(formData.primaryContactEmail)) {
        e.primaryContactEmail = 'Please enter a valid email address';
      }
      if (!formData.primaryContactPhone.trim()) {
        e.primaryContactPhone = 'Phone is required';
      }
    }

    if (key === 'partnerForm') {
      if (!formData.districtName.trim()) e.districtName = 'Organization name is required';
      if (!formData.primaryContactName.trim()) e.primaryContactName = 'Name is required';
      if (!formData.primaryContactEmail.trim()) {
        e.primaryContactEmail = 'Email is required';
      } else if (!validateEmail(formData.primaryContactEmail)) {
        e.primaryContactEmail = 'Please enter a valid email address';
      }
      if (!formData.primaryContactPhone.trim()) {
        e.primaryContactPhone = 'Phone is required';
      }
      if (!formData.partnerUseCase || formData.partnerUseCase.length < 100) {
        e.partnerUseCase = 'Please describe your use case (at least 100 characters)';
      }
      if (!formData.partnerAttestation) {
        const n = parseInt(formData.partnerAccountCount);
        if (!n || n <= 0) e.partnerAccountCount = 'Number of accounts is required for non-attesting partners';
      }
    }

    if (key === 'additionalContacts') {
      // All optional — but if they start filling one out, validate the email format
      if (formData.curriculumDirectorEmail && !validateEmail(formData.curriculumDirectorEmail)) {
        e.curriculumDirectorEmail = 'Please enter a valid email address';
      }
      if (formData.techDirectorEmail && !validateEmail(formData.techDirectorEmail)) {
        e.techDirectorEmail = 'Please enter a valid email address';
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const nextStep = () => { if (validateCurrentStep()) setStepIndex(i => Math.min(i + 1, totalSteps - 1)); };
  const prevStep = () => setStepIndex(i => Math.max(i - 1, 0));

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;
    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('data', JSON.stringify(formData));
      const response = await fetch(apiUrl('/api/districts/signup'), { method: 'POST', body: submitData });
      if (!response.ok) throw new Error('Failed to submit signup');
      setSubmitted(true);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to submit. Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ----- Thank You -----
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <StandaloneHeader />
        <div className="py-16 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <Card className="p-8">
              <CalendarCheck className="h-16 w-16 mx-auto text-primary mb-6" />
              <h2 className="text-3xl font-bold font-serif mb-4">Thank you!</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Your signup has been received. Here's what happens next:
              </p>
              <div className="text-left space-y-4 bg-primary/5 rounded-lg p-6 mb-8">
                <div className="flex gap-3">
                  <span className="font-bold text-primary shrink-0">1.</span>
                  <p>We'll reach out within 1-2 business days to schedule a <strong>30-minute onboarding call</strong>.</p>
                </div>
                <div className="flex gap-3">
                  <span className="font-bold text-primary shrink-0">2.</span>
                  <p>On the call, we'll get you set up. The <strong>VPAT and accessibility documentation</strong> come in the welcome packet.</p>
                </div>
                <div className="flex gap-3">
                  <span className="font-bold text-primary shrink-0">3.</span>
                  <p>Please come prepared with the <strong>total number of students in each grade</strong> for the school(s) you'd like to onboard.</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Questions in the meantime? Reach us at{' '}
                <a href="mailto:support@readwell.com" className="text-primary underline">support@readwell.com</a>
              </p>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ----- Org Info (District / School) -----
  const renderOrgInfo = () => {
    const isDistrict = orgType === 'district';
    const nameLabel = isDistrict ? 'District Name' : 'School Name';
    const namePlaceholder = isDistrict ? 'Start typing to search NCES database...' : 'Start typing to search...';
    const enrollLabel = isDistrict
      ? (formData.programType === 'pilot' ? 'Grades 4–12 enrollment across pilot schools' : 'Grades 4–12 district enrollment')
      : 'Grades 4–12 enrollment';

    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="state">State *</Label>
          <select
            id="state"
            value={formData.state}
            onChange={(e) => { updateField('state', e.target.value); setLookupResults([]); }}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select a state...</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {errors.state && <p className="text-sm text-destructive">{errors.state}</p>}
        </div>

        <div className="space-y-2 relative">
          <Label htmlFor="districtName">{nameLabel} *</Label>
          <div className="relative">
            <Input id="districtName" value={formData.districtName}
              onChange={(e) => handleDistrictSearch(e.target.value)} placeholder={namePlaceholder}
              autoComplete="off" />
            {lookupLoading && (
              <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
          {!formData.state && formData.districtName.length > 0 && (
            <p className="text-xs text-muted-foreground">Select a state first to enable district lookup</p>
          )}
          {lookupResults.length > 0 && (
            <div className="absolute z-20 bg-white border border-border rounded-lg shadow-lg w-full mt-1 max-h-64 overflow-y-auto">
              {lookupResults.map((r) => (
                <button
                  key={r.leaid}
                  type="button"
                  onClick={() => selectDistrict(r)}
                  className="w-full text-left px-4 py-3 hover:bg-accent transition-colors border-b border-border last:border-0"
                >
                  <div className="font-medium text-sm">{r.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {r.city}, {r.state} {r.county ? `· ${r.county}` : ''} · {(r.totalEnrollment || 0).toLocaleString()} total students
                  </div>
                </button>
              ))}
            </div>
          )}
          {errors.districtName && <p className="text-sm text-destructive">{errors.districtName}</p>}
        </div>

        {isDistrict && (
          <div className="space-y-2">
            <Label>Program Type *</Label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'pilot', label: 'Pilot Program', desc: 'Start with select schools' },
                { id: 'all_schools', label: 'All Schools', desc: 'District-wide rollout' },
              ].map(opt => (
                <button key={opt.id} type="button"
                  onClick={() => { updateField('programType', opt.id); setPricingRevealed(false); }}
                  className={`rounded-lg border-2 p-4 text-left transition-all ${
                    formData.programType === opt.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`} aria-pressed={formData.programType === opt.id}>
                  <div className="font-semibold text-sm">{opt.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{opt.desc}</div>
                </button>
              ))}
            </div>
            {errors.programType && <p className="text-sm text-destructive">{errors.programType}</p>}
          </div>
        )}

        <Separator />

        <div className="space-y-2">
          <Label htmlFor="totalEnrollment">{enrollLabel} *</Label>
          <Input id="totalEnrollment" type="number" value={formData.totalEnrollment}
            onChange={(e) => { updateField('totalEnrollment', e.target.value); setPricingRevealed(false); setEnrollmentSource(''); }}
            placeholder="2500" />
          {enrollmentSource && (
            <p className="text-xs text-muted-foreground">
              <Search className="inline h-3 w-3 mr-1" />
              Pre-filled from {enrollmentSource}. Edit if needed.
            </p>
          )}
          {errors.totalEnrollment && <p className="text-sm text-destructive">{errors.totalEnrollment}</p>}
        </div>

        <div className="p-6 bg-primary/5 rounded-lg text-center">
          {!pricingRevealed ? (
            <Button type="button" onClick={() => setPricingRevealed(true)}
              disabled={!formData.totalEnrollment || parseInt(formData.totalEnrollment) <= 0}>
              <Calculator className="h-4 w-4 mr-2" />
              Check License Fee
            </Button>
          ) : (
            <>
              <p className="text-sm font-semibold mb-1">Estimated Annual License Fee</p>
              <p className="text-4xl font-bold text-primary">
                {enrollmentPricing.price === 0 ? 'FREE' : `$${enrollmentPricing.price.toLocaleString()}`}
              </p>
              <p className="text-xs text-muted-foreground mt-2">{enrollmentPricing.program}</p>
            </>
          )}
        </div>
      </div>
    );
  };

  // ----- Primary Contact -----
  const renderPrimaryContact = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="primaryContactName">Full Name *</Label>
        <Input id="primaryContactName" value={formData.primaryContactName}
          onChange={(e) => updateField('primaryContactName', e.target.value)} placeholder="John Smith" />
        {errors.primaryContactName && <p className="text-sm text-destructive">{errors.primaryContactName}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="primaryContactTitle">Title / Position</Label>
        <Input id="primaryContactTitle" value={formData.primaryContactTitle}
          onChange={(e) => updateField('primaryContactTitle', e.target.value)} placeholder="Superintendent" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="primaryContactEmail">Email *</Label>
        <Input id="primaryContactEmail" type="email" value={formData.primaryContactEmail}
          onChange={(e) => updateField('primaryContactEmail', e.target.value)} placeholder="jsmith@district.edu" />
        {errors.primaryContactEmail && <p className="text-sm text-destructive">{errors.primaryContactEmail}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="primaryContactPhone">Phone *</Label>
        <Input id="primaryContactPhone" type="tel" value={formData.primaryContactPhone}
          onChange={(e) => updateField('primaryContactPhone', e.target.value)} placeholder="(555) 123-4567" />
        <p className="text-xs text-muted-foreground">Format: (555) 123-4567</p>
        {errors.primaryContactPhone && <p className="text-sm text-destructive">{errors.primaryContactPhone}</p>}
      </div>
    </div>
  );

  // ----- Additional Contacts (optional) -----
  const renderAdditionalContacts = () => (
    <div className="space-y-6">
      <Alert>
        <AlertDescription>
          These contacts are optional. You can skip this step and provide them later.
        </AlertDescription>
      </Alert>

      <div>
        <h4 className="font-semibold mb-3">Director of Curriculum</h4>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="curriculumDirectorName">Name</Label>
            <Input id="curriculumDirectorName" value={formData.curriculumDirectorName}
              onChange={(e) => updateField('curriculumDirectorName', e.target.value)} placeholder="Jane Doe" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="curriculumDirectorEmail">Email</Label>
            <Input id="curriculumDirectorEmail" type="email" value={formData.curriculumDirectorEmail}
              onChange={(e) => updateField('curriculumDirectorEmail', e.target.value)} placeholder="jdoe@district.edu" />
            {errors.curriculumDirectorEmail && <p className="text-sm text-destructive">{errors.curriculumDirectorEmail}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="curriculumDirectorPhone">Phone</Label>
            <Input id="curriculumDirectorPhone" type="tel" value={formData.curriculumDirectorPhone}
              onChange={(e) => updateField('curriculumDirectorPhone', e.target.value)} placeholder="(555) 123-4567" />
          </div>
        </div>
      </div>

      <Separator />

      <div>
        <h4 className="font-semibold mb-3">Director of Technology</h4>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="techDirectorName">Name</Label>
            <Input id="techDirectorName" value={formData.techDirectorName}
              onChange={(e) => updateField('techDirectorName', e.target.value)} placeholder="Mike Johnson" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="techDirectorEmail">Email</Label>
            <Input id="techDirectorEmail" type="email" value={formData.techDirectorEmail}
              onChange={(e) => updateField('techDirectorEmail', e.target.value)} placeholder="mjohnson@district.edu" />
            {errors.techDirectorEmail && <p className="text-sm text-destructive">{errors.techDirectorEmail}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="techDirectorPhone">Phone</Label>
            <Input id="techDirectorPhone" type="tel" value={formData.techDirectorPhone}
              onChange={(e) => updateField('techDirectorPhone', e.target.value)} placeholder="(555) 123-4567" />
          </div>
        </div>
      </div>
    </div>
  );

  // ----- Partner Form (single page) -----
  const renderPartnerForm = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="districtName">Organization Name *</Label>
        <Input id="districtName" value={formData.districtName}
          onChange={(e) => updateField('districtName', e.target.value)} placeholder="Reading Futures Foundation" />
        {errors.districtName && <p className="text-sm text-destructive">{errors.districtName}</p>}
      </div>

      <Separator />

      <h4 className="font-semibold">Your Contact Info</h4>
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="primaryContactName">Full Name *</Label>
          <Input id="primaryContactName" value={formData.primaryContactName}
            onChange={(e) => updateField('primaryContactName', e.target.value)} placeholder="John Smith" />
          {errors.primaryContactName && <p className="text-sm text-destructive">{errors.primaryContactName}</p>}
        </div>
        <div className="space-y-1">
          <Label htmlFor="primaryContactEmail">Email *</Label>
          <Input id="primaryContactEmail" type="email" value={formData.primaryContactEmail}
            onChange={(e) => updateField('primaryContactEmail', e.target.value)} placeholder="john@readingfutures.org" />
          {errors.primaryContactEmail && <p className="text-sm text-destructive">{errors.primaryContactEmail}</p>}
        </div>
        <div className="space-y-1">
          <Label htmlFor="primaryContactPhone">Phone *</Label>
          <Input id="primaryContactPhone" type="tel" value={formData.primaryContactPhone}
            onChange={(e) => updateField('primaryContactPhone', e.target.value)} placeholder="(555) 123-4567" />
          <p className="text-xs text-muted-foreground">Format: (555) 123-4567</p>
          {errors.primaryContactPhone && <p className="text-sm text-destructive">{errors.primaryContactPhone}</p>}
        </div>
      </div>

      <Separator />

      <div className="space-y-2">
        <Label htmlFor="partnerUseCase">How will your organization use Readwell? *</Label>
        <textarea id="partnerUseCase" value={formData.partnerUseCase}
          onChange={(e) => updateField('partnerUseCase', e.target.value)}
          placeholder="Describe the students you serve and how Readwell fits into your programming. Minimum 100 characters."
          className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        <p className="text-xs text-muted-foreground">{formData.partnerUseCase.length} / 100 characters minimum</p>
        {errors.partnerUseCase && <p className="text-sm text-destructive">{errors.partnerUseCase}</p>}
      </div>

      <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-accent/50">
        <input type="checkbox" checked={formData.partnerAttestation}
          onChange={(e) => updateField('partnerAttestation', e.target.checked)} className="mt-1 h-4 w-4" />
        <span className="text-sm">
          I attest that this organization (a) serves students with diagnosed reading disabilities
          or equivalent challenges, and (b) will not use Readwell to fulfill graded school
          assignments or formal curriculum requirements. I understand that if either condition
          changes, the organization will convert to standard pricing ($1/account/year).
        </span>
      </label>

      {!formData.partnerAttestation && (
        <div className="space-y-2">
          <Label htmlFor="partnerAccountCount">How many user accounts do you need? *</Label>
          <Input id="partnerAccountCount" type="number" value={formData.partnerAccountCount}
            onChange={(e) => updateField('partnerAccountCount', e.target.value)} placeholder="25" />
          <p className="text-xs text-muted-foreground">
            One account = one user. Accounts can run reading groups, public sessions, etc. No credential sharing.
          </p>
          {errors.partnerAccountCount && <p className="text-sm text-destructive">{errors.partnerAccountCount}</p>}
        </div>
      )}

      <div className="p-4 bg-primary/5 rounded-lg text-center">
        <p className="text-sm font-semibold mb-1">Estimated Annual Fee</p>
        <p className="text-2xl font-bold text-primary">
          {partnerPrice.price === 0 ? 'FREE' : `$${partnerPrice.price.toLocaleString()}`}
        </p>
        <p className="text-xs text-muted-foreground">{partnerPrice.program}</p>
      </div>
    </div>
  );

  // ----- Review -----
  const renderReview = () => {
    const isPartner = orgType === 'partner';
    const price = isPartner ? partnerPrice : enrollmentPricing;
    const orgLabel = orgType === 'district' ? 'District' : orgType === 'school' ? 'School' : 'Organization';

    return (
      <div className="space-y-6">
        <div className="text-center">
          <CheckCircle2 className="h-16 w-16 mx-auto text-primary mb-4" />
          <h3 className="text-2xl font-bold mb-2">Review Your Information</h3>
        </div>
        <Separator />
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold mb-1">{orgLabel}</h4>
            <p className="text-sm text-muted-foreground">
              {formData.districtName}{formData.state ? ` — ${formData.state}` : ''}
              {!isPartner && (<><br />{parseInt(formData.totalEnrollment).toLocaleString()} students
                {orgType === 'district' && formData.programType ? ` (${formData.programType === 'pilot' ? 'Pilot' : 'All Schools'})` : ''}
              </>)}
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-semibold mb-1">Estimated Annual Fee</h4>
            <p className="text-2xl font-bold text-primary">
              {price.price === 0 ? 'FREE' : `$${price.price.toLocaleString()}`}
            </p>
            <p className="text-xs text-muted-foreground">{price.program}</p>
          </div>
          <Separator />
          <div>
            <h4 className="font-semibold mb-1">Primary Contact</h4>
            <p className="text-sm text-muted-foreground">
              {formData.primaryContactName}{formData.primaryContactTitle ? ` — ${formData.primaryContactTitle}` : ''}<br />
              {formData.primaryContactEmail} | {formData.primaryContactPhone}
            </p>
          </div>
          {!isPartner && (formData.curriculumDirectorName || formData.techDirectorName) && (
            <>
              <Separator />
              <div>
                <h4 className="font-semibold mb-1">Additional Contacts</h4>
                {formData.curriculumDirectorName && (
                  <p className="text-sm text-muted-foreground">
                    Curriculum: {formData.curriculumDirectorName} — {formData.curriculumDirectorEmail}
                  </p>
                )}
                {formData.techDirectorName && (
                  <p className="text-sm text-muted-foreground">
                    Technology: {formData.techDirectorName} — {formData.techDirectorEmail}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="notes">Additional Notes (Optional)</Label>
          <textarea id="notes" value={formData.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            placeholder="Anything else we should know..."
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </div>
      </div>
    );
  };

  const renderStep = () => {
    switch (currentStepKey) {
      case 'orgInfo':            return renderOrgInfo();
      case 'primaryContact':     return renderPrimaryContact();
      case 'additionalContacts': return renderAdditionalContacts();
      case 'partnerForm':        return renderPartnerForm();
      case 'review':             return renderReview();
      default:                   return null;
    }
  };

  const isLastStep = stepIndex === totalSteps - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10">
      <StandaloneHeader />
      <div className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <Card>
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-3xl font-serif">Sign up for Readwell</CardTitle>
              <CardDescription className="mb-4">
                Get started with Readwell for your organization
              </CardDescription>
              {/* Org type selector — always visible */}
              <div className="flex justify-center gap-2 pt-2">
                {ORG_TYPES.map(opt => (
                  <button key={opt.id} type="button" onClick={() => setOrgType(opt.id)}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                      orgType === opt.id
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'bg-secondary text-muted-foreground hover:text-foreground'
                    }`}
                    aria-pressed={orgType === opt.id}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </CardHeader>

            <CardContent>
              <div className="mb-6 mt-2">
                <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                  <div className="bg-primary h-full transition-all duration-300"
                    style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }} />
                </div>
                <p className="text-sm text-center mt-2 text-muted-foreground">
                  Step {stepIndex + 1} of {totalSteps}: {STEP_TITLES[currentStepKey]}
                </p>
              </div>

              <div className="min-h-[400px]">{renderStep()}</div>

              <div className="flex justify-between mt-8 pt-6 border-t">
                {stepIndex > 0 ? (
                  <Button variant="outline" onClick={prevStep}>
                    <ChevronLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                ) : <div />}

                {!isLastStep ? (
                  <Button onClick={nextStep}>
                    {currentStepKey === 'additionalContacts' ? 'Skip / Continue' : 'Next'}
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Submitting...' : 'Submit'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function StandaloneHeader() {
  return (
    <header className="flex items-center px-6 py-4 border-b bg-background/80 backdrop-blur">
      <Link to="/" className="flex items-center space-x-2 hover:opacity-80">
        <BookOpen className="h-6 w-6" />
        <div className="flex flex-col">
          <span className="font-bold text-lg leading-none font-serif">readwell</span>
          <span className="text-xs text-muted-foreground">K-12 Platform</span>
        </div>
      </Link>
    </header>
  );
}
